import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';

import { SnCamera } from '@lib/api/entities';
import { SnCurrentOrganization, SnCurrentUser } from '@lib/auth';
import { snFadeIn, snFadeOut } from '@lib/common/animations';
import { SnOrientation, SnScreenService } from '@lib/common/services';
import { SnPlayerGroupReference } from '@lib/player/common';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

import { SnCameraGridActionsService } from '../../services/camera-grid-actions.service';

@Component({
  selector: 'sn-operational-grid-view',
  templateUrl: './operational-grid-view.component.html',
  styleUrls: ['./operational-grid-view.component.scss'],
  animations: [snFadeIn, snFadeOut],
})
export class OperationalGridViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  get cameras(): SnCamera[] {
    return this._cameras;
  }
  set cameras(value: SnCamera[]) {
    this._cameras = value.slice();
    if (this.meta?.axonDevices) {
      this.meta.axonDevices.forEach((device) => {
        const camera = new SnCamera({
          id: device.axonDeviceId,
          isAxonCamera: true,
          axonDevice: device,
        });
        this._cameras.push(camera);
      });
    }
  }

  get displayedCameras(): SnCamera[] {
    return this._displayedCameras;
  }

  get screenOrientation(): SnOrientation {
    return this._screenService.orientation;
  }

  @Input()
  public cols = 3;

  @Input()
  public rows = 3;

  @Input()
  public meta: any;

  @Input()
  public autoplay = true;

  @Input()
  public dynamic = false;

  @Input()
  public pageCount = 0;

  @Input()
  public page: number = 0;

  @Input()
  public allowTimeout = true;

  @Input()
  public hideBottom: boolean;

  @Output()
  public readonly cameraStreamPlay = new EventEmitter<{ live: boolean; camera: SnCamera }>();

  @Output()
  public readonly cameraStreamPause = new EventEmitter<SnCamera>();

  @Output()
  public readonly cameraStreamStop = new EventEmitter<SnCamera>();

  @Output()
  public readonly nextClick = new EventEmitter<Number>();

  @Output()
  public readonly previousClick = new EventEmitter<Number>();

  @Output()
  public readonly gridSession = new EventEmitter<boolean>();

  public isPlaying = true;

  public interval: any = null;
  public timeoutGrid: boolean = false;
  public isSessionActive: boolean = true;

  private readonly _destroy$ = new Subject<void>();

  private _cameras: SnCamera[] = [];
  private _displayedCameras: SnCamera[] = [];
  private _cloudVideoTimeout;

  private _pageIndex = 0;
  private _pageSize = 5;

  private _playerGroup: SnPlayerGroupReference;

  constructor(
    private readonly _screenService: SnScreenService,
    private readonly _camaraGridService: SnCameraGridActionsService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _currentUser: SnCurrentUser,
    private readonly _dialogService: SnDialogService,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {
    this._cloudVideoTimeout = this._currentOrganization.settings.cloudVideoTimeout;
  }

  get pageSize(): number {
    return this._pageSize;
  }

  @HostListener('document:click', ['event'])
  @HostListener('document:mousedown', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  @HostListener('document:mouseup', ['$event'])
  @HostListener('document:touchend', ['$event'])
  @HostListener('document:contextmenu', ['$event'])
  @HostListener('window:mousemove', ['$event'])
  @HostListener('window:touchmove', ['$event'])
  @HostListener('window:keydown', ['$event'])
  @HostListener('window:keyup', ['$event'])
  @HostListener('wheel', ['$event'])
  public resetTimer(event: MouseEvent | TouchEvent | WheelEvent): void {
    if (this._currentUser.isVideoWallUser || this.timeoutGrid) {
      return;
    }
    this._camaraGridService.stopTimer();
    this._camaraGridService.startTimer();
  }

  @HostListener('document:click')
  public documentClick(): void {
    this._camaraGridService.setTimerSlideShow(null);
    clearInterval(this.interval);
  }

  public ngOnChanges(): void {
    this.isPlaying = this.autoplay;
    this._updateDisplayedCameras();
  }

  public ngOnInit(): void {
    this._camaraGridService.time$.pipe(takeUntil(this._destroy$)).subscribe((res) => {
      clearInterval(this.interval);
      if (res !== 0) {
        this.interval = this._autoSwip(res);
      }
    });
    this._initTimeoutTimer();

    const canChange = this._camaraGridService.canChange;
    const videoWall = this._currentUser.isVideoWallUser;
    if (this.cols > 7 && !canChange && !videoWall) {
      this._updateDisplayedCameras();
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackCamera(index: number, item: SnCamera): any {
    return item ? item.id : undefined;
  }

  public onPlayerGroupInit(reference: SnPlayerGroupReference): void {
    this._playerGroup = reference;
  }

  public onDragDropped(event: CdkDragDrop<number>): void {
    const prevIndex = event.item?.data;
    const nextIndex = event.container?.data;

    const previousCamera = this.displayedCameras[prevIndex];
    this.displayedCameras[prevIndex] = this.displayedCameras[nextIndex];
    this.displayedCameras[nextIndex] = previousCamera;

    const prevCamera = this._cameras[prevIndex];
    this._cameras[prevIndex] = this._cameras[nextIndex];
    this._cameras[nextIndex] = prevCamera;
    this._updateDisplayedCameras();
  }

  public onCameraStreamPlay(live: boolean, camera: SnCamera): void {
    this.cameraStreamPlay.emit({ live, camera });
  }

  public onCameraStreamPause(camera: SnCamera): void {
    this.cameraStreamPause.emit(camera);
  }

  public onCameraStreamStop(camera: SnCamera): void {
    this.cameraStreamStop.emit(camera);
  }

  public playAll(): void {
    if (this._playerGroup) {
      this._playerGroup.play();
      this.isPlaying = true;
    }
  }

  public pauseAll(): void {
    if (this._playerGroup) {
      this._playerGroup.pause();
      this.isPlaying = false;
    }
  }

  public destroyAll(): void {
    if (this._playerGroup) {
      this._playerGroup.destroy(true);
    }
    this._cd.detectChanges();
  }

  public reinitialize(): void {
    if (this._playerGroup) {
      this._playerGroup.initialize();
      this._updateDisplayedCameras();
      this.isSessionActive = true;
      this.timeoutGrid = false;
      this._camaraGridService.startTimer();
      this.gridSession.next(true);
      this._cd.detectChanges();
    }
    this._cd.detectChanges();
  }

  public hasPrevPage(): boolean {
    const pageIndex = this.dynamic ? this.page - 1 : this._pageIndex;
    return pageIndex >= 1 && this._pageSize !== 0;
  }

  public hasNextPage(): boolean {
    const numberOfPages = this._getNumberOfPages();
    const page = this.dynamic ? this.page : this._pageIndex;
    return page < numberOfPages && this._pageSize !== 0;
  }

  public nextPage(): void {
    if (!this.hasNextPage()) {
      this._pageIndex = 0;
      this._updateDisplayedCameras();

      return;
    }
    if (this.dynamic) {
      this.nextClick.emit(+this.page + 1);
    } else {
      this._pageIndex++;
      this._updateDisplayedCameras();
    }
  }

  public prevPage(): void {
    if (!this.hasPrevPage()) {
      return;
    }
    if (this.dynamic) {
      this.previousClick.emit(this.page - 1);
    } else {
      this._pageIndex--;
      this._updateDisplayedCameras();
    }
  }

  public onCameraSelected(selectedCamera: SnCamera, currentCamera: SnCamera): void {
    const index = this._cameras.findIndex((camera) => camera.id === currentCamera.id);
    if (index > -1) {
      this._cameras[index] = selectedCamera;
      this._displayedCameras[index] = selectedCamera;
      if (selectedCamera.descendantCameras?.length) {
        this._displayedCameras.splice(index, 0, ...selectedCamera.descendantCameras);
      }
      this._updateDisplayedCameras();
      this._cd.detectChanges();
    }
  }

  private _autoSwip(time: number): any {
    return setInterval(() => {
      this.nextPage();
    }, time);
  }

  private _getNumberOfPages(): number {
    return this.dynamic ? this.pageCount : Math.ceil(this._cameras.length / this._pageSize) - 1;
  }

  private _updateDisplayedCameras(): void {
    const pageSize = this._pageSize;
    const offset = 1;
    const startIndex = this._pageIndex * pageSize + offset;
    const displayedCameras = [
      this._cameras[0],
      ...this.cameras.slice(0, this.cameras.length).splice(startIndex, pageSize),
    ];
    this._displayedCameras = Array(pageSize);
    this._displayedCameras.splice(0, displayedCameras.length, ...displayedCameras);
    this._cd.detectChanges();
  }

  private _initTimeoutTimer(): void {
    if (this._currentUser.isVideoWallUser) {
      return;
    }
    this._camaraGridService
      .initTimeoutTimer()
      .pipe(takeUntil(this._destroy$))
      .subscribe((time) => {
        const now = time * 1000;
        if (now >= this._cloudVideoTimeout * 60000) {
          this.timeoutGrid = true;
          this._camaraGridService.stopTimer();
          this._openTimeoutDialog();
        }
      });
  }

  private _openTimeoutDialog(): void {
    this._dialogService
      .timeoutMessage({
        data: {
          message: t('cameras.grid.timeoutMessage'),
        },
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((confirm) => {
        if (!confirm) {
          this.destroyAll();
          this.isSessionActive = false;
          this.gridSession.next(false);
        } else {
          this.timeoutGrid = false;
          this._camaraGridService.startTimer();
          this.isSessionActive = true;
          this.gridSession.next(true);
        }
      });
  }
}
